import { useEffect, useMemo, useState } from "react";
import { Search, Users } from "lucide-react";
import { LANGS } from "./GroupVersionEditor";

const PAID = ["starter", "pro", "pro_plus"];
const PLANS = ["all", "paid", "free", ...PAID];
const TIER_LABEL = { free: "Free", starter: "Starter", pro: "Pro", pro_plus: "Pro+" };
const ctl = "h-8 rounded-md bg-slate-900 border border-slate-700 px-2 text-xs text-slate-100";

function matchesFilters(u, f) {
  if (f.plan === "paid" && !PAID.includes(u.tier)) return false;
  if (!["all", "paid"].includes(f.plan) && u.tier !== f.plan) return false;
  if (f.lang !== "all" && u.lang !== f.lang) return false;
  if (f.verifiedOnly && !u.email_verified) return false;
  return !(f.hideTest && u.is_test);
}

function Filters({ f, setF, t }) {
  const toggle = (key) => (e) => setF({ ...f, [key]: e.target.checked });
  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="group-filters">
      <select value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} className={ctl} data-testid="group-filter-plan">
        {PLANS.map((p) => <option key={p} value={p}>{TIER_LABEL[p] || t(`groupEmail.plan_${p}`)}</option>)}
      </select>
      <select value={f.lang} onChange={(e) => setF({ ...f, lang: e.target.value })} className={ctl} data-testid="group-filter-lang">
        <option value="all">{t("groupEmail.langAll")}</option>
        {LANGS.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
      </select>
      <label className="flex items-center gap-1.5 text-xs text-slate-300">
        <input type="checkbox" checked={f.verifiedOnly} onChange={toggle("verifiedOnly")} className="accent-amber-500" data-testid="group-filter-verified" />{t("groupEmail.verifiedOnly")}
      </label>
      <label className="flex items-center gap-1.5 text-xs text-slate-300">
        <input type="checkbox" checked={f.hideTest} onChange={toggle("hideTest")} className="accent-amber-500" data-testid="group-filter-hide-test" />{t("groupEmail.hideTest")}
      </label>
    </div>
  );
}

function AudienceRow({ u, checked, onToggle, t }) {
  const tag = "text-[10px] px-1.5 py-0.5 rounded border font-mono-data";
  return (
    <label className={`flex items-center gap-2 px-2 py-1.5 rounded text-xs ${u.unsubscribed ? "opacity-50" : "hover:bg-slate-800/40 cursor-pointer"}`} data-testid={`group-row-${u.id}`}>
      <input type="checkbox" checked={checked} disabled={u.unsubscribed} onChange={() => onToggle(u.id)} className="accent-amber-500" data-testid={`group-check-${u.id}`} />
      <span className="font-mono-data text-slate-200 truncate">{u.email}</span>
      <span className="text-slate-500 truncate hidden sm:inline">{u.company_name || u.name}</span>
      <span className="ml-auto flex items-center gap-1 shrink-0">
        {u.unsubscribed && <span className={`${tag} border-red-500/40 text-red-300`} data-testid={`group-unsub-${u.id}`}>{t("groupEmail.unsubscribed")}</span>}
        {!u.email_verified && <span className={`${tag} border-slate-600 text-slate-400`}>{t("groupEmail.notVerified")}</span>}
        <span className={`${tag} border-slate-700 text-slate-300`}>{TIER_LABEL[u.tier] || u.tier}</span>
        <span className={`${tag} border-slate-700 text-slate-400`}>{u.lang.toUpperCase()}</span>
      </span>
    </label>
  );
}

export default function GroupAudiencePicker({ users, loadError, selected, onChange, t }) {
  const [f, setF] = useState({ plan: "all", lang: "all", verifiedOnly: true, hideTest: true });
  const [q, setQ] = useState("");
  const filtered = useMemo(() => users.filter((u) => matchesFilters(u, f)), [users, f]);
  const eligibleIds = useMemo(() => filtered.filter((u) => !u.unsubscribed).map((u) => u.id), [filtered]);
  useEffect(() => { onChange(new Set(eligibleIds)); }, [eligibleIds, onChange]);

  const needle = q.trim().toLowerCase();
  const shown = needle ? filtered.filter((u) => `${u.email} ${u.name} ${u.company_name}`.toLowerCase().includes(needle)) : filtered;
  const shownEligible = shown.filter((u) => !u.unsubscribed).map((u) => u.id);
  const selectShown = () => onChange(needle ? new Set([...selected, ...shownEligible]) : new Set(eligibleIds));
  const toggle = (id) => { const next = new Set(selected); next.has(id) ? next.delete(id) : next.add(id); onChange(next); };

  return (
    <div className="tech-card p-4 space-y-3" data-testid="group-audience">
      <div className="label-tech text-slate-400 flex items-center gap-2"><Users size={14} />{t("groupEmail.audienceTitle")}</div>
      <Filters f={f} setF={setF} t={t} />
      <div className="relative">
        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("groupEmail.search")} className={`${ctl} w-full pl-8`} data-testid="group-search" />
      </div>
      <div className="flex flex-wrap items-center gap-3 text-xs">
        <button onClick={selectShown} className="text-amber-400 hover:text-amber-300" data-testid="group-select-all">{t("groupEmail.selectAll", { n: shownEligible.length })}</button>
        <button onClick={() => onChange(new Set())} className="text-slate-400 hover:text-slate-200" data-testid="group-select-none">{t("groupEmail.selectNone")}</button>
        <span className="ml-auto font-mono-data text-slate-400" data-testid="group-selected-count">{t("groupEmail.selectedCount", { selected: selected.size, shown: filtered.length })}</span>
      </div>
      <div className="max-h-80 overflow-y-auto border-t border-slate-800 pt-2 space-y-0.5" data-testid="group-audience-list">
        {shown.map((u) => <AudienceRow key={u.id} u={u} checked={selected.has(u.id)} onToggle={toggle} t={t} />)}
        {!shown.length && !loadError && <div className="text-xs text-slate-500 px-2 py-3" data-testid="group-no-users">{t("groupEmail.noUsers")}</div>}
        {loadError && <div role="alert" className="text-xs text-red-300 px-2 py-3" data-testid="group-audience-error">{loadError}</div>}
      </div>
    </div>
  );
}
